import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {dispatchKeys} from 'src/dispatch/query-keys'
import {invalidateEquipmentMoves} from 'src/project-deployed-equipment/invalidate-equipment-moves'

import {getJob, listJobs, patchJob, reportJobDidNotHappen} from './api'
import {jobKeys} from './query-keys'
import type {JobListFilters, JobPatchInput} from './schemas'

export function useJobs(filters: JobListFilters = {}, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: jobKeys.list(filters),
        queryFn: () => listJobs(filters),
        enabled: options?.enabled ?? true,
    })
}

export function useRecentJobsByCustomer(customerId: string | null) {
    return useJobs(
        {
            customerId: customerId ?? undefined,
            sortBy: 'created_at',
            sortDir: 'desc',
        },
        {enabled: Boolean(customerId)},
    )
}

export function useJob(jobId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: jobKeys.detail(jobId),
        queryFn: () => getJob(jobId),
        enabled: options?.enabled ?? Boolean(jobId),
    })
}

export function useInvalidateJobs() {
    const queryClient = useQueryClient()
    return () => void queryClient.invalidateQueries({queryKey: jobKeys.all})
}

export function useJobMutation<TArgs extends unknown[]>(
    mutationFn: (...args: TArgs) => Promise<unknown>,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (...args: TArgs) => mutationFn(...args),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
        },
    })
}

/** Invalidate equipment move and driver queries after a job change. `projectId` may be null. */
export function useInvalidateJobChange() {
    const queryClient = useQueryClient()

    return (projectId: string | null) => {
        void invalidateEquipmentMoves(queryClient, projectId)
        void queryClient.invalidateQueries({queryKey: dispatchKeys.drivers()})
    }
}

export function usePatchJob() {
    const invalidate = useInvalidateJobChange()

    return useMutation({
        mutationFn: ({jobId, input}: {jobId: string; input: JobPatchInput}) =>
            patchJob(jobId, input),
        onSuccess: (job) => {
            invalidate(job.projectId)
        },
    })
}

export function useReportJobDidNotHappen() {
    const invalidate = useInvalidateJobChange()

    return useMutation({
        mutationFn: ({jobId, note}: {jobId: string; note?: string}) =>
            reportJobDidNotHappen(jobId, note),
        onSuccess: () => {
            invalidate(null)
        },
    })
}
